import React, { useState, useEffect, useRef } from 'react';
import "./chatbot.css"
import { useStateMachine } from 'little-state-machine';
import { setChatHistory } from './chatbotAction.ts';
import MarkdownIt from 'markdown-it';
import MarkdownItExternalLinks from 'markdown-it-external-links';
import { userAppContext } from '../../Context/Context';
import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  PaperAirplaneIcon,
  CheckIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import StopIcon from '@heroicons/react/24/solid/StopIcon';

export default function ChatBot({
  chatId = 'default',
  message = '',
  question = '',
  shortQuestion = '',
  autoSend = false,
  style = {},
}) {
  const { isAuthenticated, userApiKey, groups, user } = userAppContext();
  const { actions, state } = useStateMachine({ actions: { setChatHistory } });
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [downloadId, setDownloadId] = useState(null);
  const [lastActivityTime, setLastActivityTime] = useState(new Date().getTime());
  const [inputMessage, setInputMessage] = useState('');
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isBotResponding, setIsBotResponding] = useState(false);
  const [isChatActive, setIsChatActive] = useState(false);
  const [hasAutoSent, setHasAutoSent] = useState(false);
  const [botStatus, setBotStatus] = useState(null); // 'planning', 'thinking', 'typing'

  const localStorageKey = `chatMessages-${chatId}`;
  const userName = user?.['custom:name'];

  const initialBotMessage = {
    id: 'bot',
    sender: 'bot',
    message:
      message !== ''
        ? message
        : 'Hello! How can I help you?\nDo you want to see what kind of questions I can help you answer?',
    timestamp: new Date(),
  };

  // if no previous history, set initial message
  useEffect(() => {
    if (state?.chatHistory && state?.chatHistory.length === 0) {
      actions.setChatHistory([initialBotMessage]);
    }
  }, []);

  const [messages, setMessages] = useState(() => {
    const savedMessages = localStorage.getItem(localStorageKey);
    return savedMessages
      ? JSON.parse(savedMessages).map(message => ({ ...message, timestamp: new Date(message.timestamp) }))
      : [initialBotMessage];
  });

  const messagesEndRef = useRef(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // scroll timer
  useEffect(() => {
    scrollToBottom();
    localStorage.setItem(localStorageKey, JSON.stringify(messages));
  }, [messages, localStorageKey]);

  const resetInactivityTimeout = () => {
    setLastActivityTime(new Date().getTime());
  };

  // inactivity timer
  useEffect(() => {
    const checkInactivity = setInterval(() => {
      const currentTime = new Date().getTime();
      if (currentTime - lastActivityTime >= 3600000) {
        handleEndChat(null, userApiKey);
      }
    }, 600000);

    return () => clearInterval(checkInactivity);
  }, [lastActivityTime, userApiKey]);

  const stopTyping = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsBotResponding(false);
    setIsBotTyping(false);
    setBotStatus(null);
  };

  const handleSendMessage = async (e, userApiKey) => {
    const messageToSend = (question || inputMessage).trim();

    if (messageToSend === '') return;

    const currentTime = new Date();
    const userMessage = {
      id: `user-${currentTime.getTime()}`,
      sender: 'user',
      message: messageToSend,
      timestamp: currentTime,
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    actions.setChatHistory(prevChatHistory => [...prevChatHistory, userMessage]);
    setInputMessage('');
    setIsBotTyping(true);
    resetInactivityTimeout();
    setIsChatActive(true);
    scrollToBottom();
    await handleReceiveMessage(userMessage.message, userApiKey);
  };

  const abortControllerRef = useRef(null);

  const handleReceiveMessage = async (userMessage, userApiKey) => {
    const currentTime = new Date();
    const url = `${process.env.REACT_APP_URL_CHAT_API}get?msg=${encodeURIComponent(userMessage)}`;
    const headers = { Authorization: userApiKey };

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Start with "planning" status
    setBotStatus('planning');
    setIsBotTyping(true);

    // Transition to "thinking" after a short delay
    const thinkingTimeout = setTimeout(() => {
      if (!abortController.signal.aborted) {
        setBotStatus('thinking');
      }
    }, 2000);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: abortController.signal,
        keepalive: true,
      });

      clearTimeout(thinkingTimeout);

      if (!response.ok) {
        if (response.status === 504) {
          throw new Error('The request is taking longer than expected. Please try a simpler question or try again later.');
        }
        throw new Error('Network response was not ok');
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      let botMessageId = `bot-${currentTime.getTime()}`;
      let botMessageText = '';

      setIsBotTyping(true);
      setIsBotResponding(true);

      setMessages(prevMessages => [
        ...prevMessages,
        { id: botMessageId, sender: 'bot', message: '', timestamp: new Date() },
      ]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      actions.setChatHistory(prev => [
        ...prev,
        { id: botMessageId, sender: 'bot', message: '', timestamp: new Date() },
      ]);

      // Read the stream
      let done = false;
      let isInPlanPhase = false;
      let hasSeenSeparator = false;

      while (!done) {
        if (abortController.signal.aborted) {
          console.log('⛔ Stream aborted manually before read.');
          break;
        }

        const { value, done: chunkDone } = await reader.read();
        done = chunkDone;

        if (abortController.signal.aborted) {
          console.log('⛔ Stream aborted manually after read.');
          break;
        }

        if (value) {
          let chunk = decoder.decode(value, { stream: true });

          for (let char of chunk) {
            if (abortController.signal.aborted) {
              console.log('⛔ Aborted during chunk typing');
              break;
            }
            botMessageText += char;

            // Detect phases based on content
            if (botMessageText.includes('Plan:') && !isInPlanPhase) {
              isInPlanPhase = true;
              setBotStatus('planning');
              setIsBotTyping(true);
            }

            // Detect separator between plan and execution
            if (botMessageText.includes('----------------------------------------') && !hasSeenSeparator) {
              hasSeenSeparator = true;
              setBotStatus('thinking');
              setIsBotTyping(true);
            }

            // After separator, when actual response starts
            if (hasSeenSeparator && botMessageText.split('----------------------------------------')[1]?.trim().length > 10) {
              setBotStatus('typing');
              setIsBotTyping(false);
            }

            setMessages(prevMessages => {
              const updatedMessages = [...prevMessages];
              const lastIndex = updatedMessages.findIndex(msg => msg.id === botMessageId);
              if (lastIndex !== -1) {
                updatedMessages[lastIndex] = { ...updatedMessages[lastIndex], message: botMessageText };
              }
              return updatedMessages;
            });

            await new Promise(resolve => setTimeout(resolve, 1));
          }
        }
      }
    } catch (error) {
      clearTimeout(thinkingTimeout);
      if (error.name === 'AbortError') {
        console.log('⛔ Bot response aborted');
      } else {
        console.error(error);
        const errorMessage = error.message.includes('taking longer than expected')
          ? error.message
          : 'Sorry, there was an error processing your request. Please try again.';
        
        const errorMsg = {
          id: `bot-error-${currentTime.getTime()}`,
          sender: 'bot',
          message: errorMessage,
          timestamp: new Date(),
        };
        
        setMessages(prev => [...prev, errorMsg]);
        actions.setChatHistory(prev => [...prev, errorMsg]);
      }
    } finally {
      setIsBotTyping(false);
      setIsBotResponding(false);
      setBotStatus(null);
      abortControllerRef.current = null;
    }
  };

  const handleEndChat = async (e, userApiKey) => {
    const headers = { Authorization: userApiKey };
    const urlClear = `${process.env.REACT_APP_URL_CHAT_API}clear`;

    try {
      const response = await fetch(urlClear, { method: 'GET', headers: headers, credentials: 'include' });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      actions.setChatHistory([initialBotMessage]);
      localStorage.removeItem(localStorageKey);
      setMessages([initialBotMessage]);
      setIsChatActive(false);
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        setInputMessage(inputMessage + '\n');
      } else {
        handleSendMessage(e, userApiKey);
      }
      e.preventDefault();
    }
  };

  const handleButtonClick = e => {
    if (isBotTyping || isBotResponding) {
      stopTyping();
    } else {
      handleSendMessage(e, userApiKey);
      setInputMessage('');
    }
  };

  const formatTimestamp = timestamp => {
    // Convert timestamp to a Date object if it's a string
    const date = new Date(timestamp);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid timestamp:', timestamp);
      return 'Invalid Date';
    }

    const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    return new Intl.DateTimeFormat('es-ES', options).format(date);
  };

  const formatMessage = message => {
    const md = new MarkdownIt();
    md.use(MarkdownItExternalLinks, { externalClassName: 'external-link' });

    md.renderer.rules.link_open = (tokens, idx) => {
      const href = tokens[idx].attrGet('href');
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">`;
    };

    md.renderer.rules.link_close = (tokens, idx) => {
      return ' <i class="bi bi-box-arrow-up-right"></i>&nbsp;</a>';
    };

    return md.render(message);
  };

  const copyToClipboard = (message, messageId) => {
    navigator.clipboard.writeText(message).then(
      () => {
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 1000);
      },
      err => {
        console.error('Error copying text to clipboard:', err);
      },
    );
  };

  const downloadChatAsTxt = () => {
    const textContent = messages
      ?.map(msg => {
        const time = formatTimestamp(msg.timestamp);
        return `[${time}] ${msg.sender === 'user' ? 'You' : 'Bot'}: ${msg.message}`;
      })
      ?.join('\n');

    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'chat.txt';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);

    setDownloadId(true);

    setTimeout(() => {
      setDownloadId(null);
    }, 2000);
  };

  const isAcademicUser = groups?.includes('ROLE_ACADEMIC');

  useEffect(() => {
    if (shortQuestion) {
      setInputMessage(shortQuestion);
    } else if (question) {
      setInputMessage(question);
    }
  }, [question, shortQuestion]);

  useEffect(() => {
    if (autoSend && question && !hasAutoSent && isAuthenticated && userApiKey) {
      setHasAutoSent(true);

      setTimeout(() => {
        handleSendMessage(null, userApiKey);
      }, 500);
    }
  }, [autoSend, question, hasAutoSent, isAuthenticated, userApiKey]);

  return isAuthenticated && !isAcademicUser ? (
    <React.Fragment>
      <div className="pt-2 container overflow-bot">
        <div>
          <div className="text-center px-3 pb-2">
            <small className="text-muted">
              This assistant uses OpenAI technology and Retrieval-Augmented Generation (RAG) to provide
              responses based on DISGENET data. Outputs may contain errors or hallucinations and should be
              independently verified. For more information{' '}
              <a
                href="/documents/medbio_chatbot_disclaimer.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-underline"
              >
                click here
              </a>
              .
            </small>
          </div>
        </div>
        <div className="chat-bot-container-aida" style={{ height: isChatActive ? '100vh' : 'auto' }}>
          <div className="pl-6 pr-6 pt-6 row justify-content-center">
            <div className="chat-bot-wrapper">
              <div className="chat-bot-container scroll-fade-container">
                <div className="chat-bot-messages" style={{ maxHeight: isChatActive ? 'calc(100vh - 200px)' : '490px' }}>
                  {messages?.map((message, index) => (
                    <React.Fragment key={`message-${index}`}>
                      {message.sender === 'user' && (
                        <div>
                          <div className="d-flex justify-content-end">
                            <p className="user-name">{userName}</p>
                            <p className="pl-2"> {formatTimestamp(message.timestamp)}</p>
                          </div>
                          <div className="bot-message-content user-message">
                            <div
                              className="bot-text-user scroll-container"
                              dangerouslySetInnerHTML={{ __html: formatMessage(message.message) }}
                            />
                          </div>
                        </div>
                      )}
                      {message.sender === 'bot' && (
                        <div>
                          <div className="timestamp-message">{formatTimestamp(message.timestamp)}
                            <p className="blog-sub-subtitle ml-2">AIDA </p>
                          </div>
                          <div className="bot-message received">
                            <div>
                              <img
                                src="../../static/images/eyesAida.svg"
                                alt="bot-aida"
                                width="50"
                                height="50"
                              />
                            </div>
                            <div className="bot-message-content">
                              <div
                                className="bot-text"
                                dangerouslySetInnerHTML={{ __html: formatMessage(message.message) }}
                              />

                              <br></br>
                              {message.message !== initialBotMessage.message && message.isValid && (
                                <button
                                  className="copy-bot-btn"
                                  onClick={() => copyToClipboard(message.message, message.id)}
                                >
                                  {copiedMessageId === message.id ? (
                                    <span>
                                      Copied <ClipboardDocumentCheckIcon className="h-5 w-5 text-white inline-block" />
                                    </span>
                                  ) : (
                                    <span>
                                      Copy <ClipboardDocumentIcon className="h-5 w-5 text-white inline-block" />
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                  <div ref={messagesEndRef} />
                  {isBotTyping && botStatus && (
                    <div className="bot-message received pt-4">
                      <div>
                        <img
                          className="pt-2"
                          src="../../static/images/eyesAida.svg"
                          alt="bot-aida"
                          width="50"
                          height="50"
                        />
                      </div>

                      <div className="bot-message-content mt-1">
                        <i>
                          {botStatus === 'planning' && 'planning...'}
                          {botStatus === 'thinking' && 'thinking...'}
                          {botStatus === 'typing' && 'typing...'}
                        </i>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>
        <div>
          <div className="chat-controls">
            <div className="mt-auto p-2 d-flex">
              <div className="chat-input-container">
                <textarea
                  className="chat-bot-input mt-1"
                  placeholder="Type your question..."
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button
                  className="button-chat-bot"
                  aria-label={isBotResponding ? 'Stop response' : 'Send message'}
                  onClick={handleButtonClick}
                >
                  {isBotTyping || isBotResponding ? (
                    <StopIcon className="h-6 w-6 text-white" />
                  ) : (
                    <PaperAirplaneIcon className="h-6 w-6 text-white" style={{ transform: 'rotate(-45deg)' }} />
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="chat-actions">
            <div className="end-chat">
              <button className=" btn-gradient-pink button-chat" onClick={e => handleEndChat(e, userApiKey)}>
                Clear Chat
              </button>
            </div>
            <div className="mr-4">
              <button className=" btn-gradient-pink button-chat" onClick={downloadChatAsTxt}>
                {downloadId ? (
                  <span>
                    Download <CheckIcon className="h-5 w-5 text-white inline-block" />
                  </span>
                ) : (
                  <span>Download Chat</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <div className="container overflow-bot">
        <div className="p-2 row justify-content-center">
          <div className="chat-bot-wrapper">
            <div className="chat-bot-container">
              <div className="chat-bot-messages">
                <React.Fragment>
                  {isAcademicUser ? (
                    <div className="bot-message received d-flex justify-content-start">
                      <div>
                        <img src="../../static/images/eyesAida.svg" alt="bot-aida" width="50" height="50" />
                      </div>
                      <p className="bot-message-content">
                        As an academic user, you do not have access to the chat. Please explore other options
                        available in{' '}
                        <a className="" href="/plans" target="_blank" rel="noopener noreferrer">
                          <u>Plans</u>.
                        </a>{' '}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="bot-message received d-flex justify-content-start">
                        <div>
                          <img src="../../static/images/eyesAida.svg" alt="bot-aida" width="50" height="50" />
                        </div>
                        <p className="bot-message-content">
                          Hello! You need to be logged in to access the chat
                        </p>
                      </div>
                      <div className="bot-message received d-flex justify-content-start">
                        <div>
                          <img src="../../static/images/eyesAida.svg" alt="bot-aida" width="50" height="50" />
                        </div>
                        <p className="bot-message-content">
                          Access:{' '}
                          <a className="" href="/login" target="_blank" rel="noopener noreferrer">
                            <u>Login</u>
                          </a>
                          ,{' '}
                          <a className="" href="/plans" target="_blank" rel="noopener noreferrer">
                            <u>Register</u>
                          </a>{' '}
                          or{' '}
                          <a className="inline-flex items-center space-x-1" href="/Support" target="_blank" rel="noopener noreferrer">
                            <u>SUPPORT</u>
                            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
